import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import styles from './styles.module.scss';

const ScrollableDescription = ({ children }) => {
  const contentRef = useRef(null);
  const trackRef = useRef(null);
  const [thumbHeight, setThumbHeight] = useState(0);
  const [thumbTop, setThumbTop] = useState(0);
  const [needsScroll, setNeedsScroll] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);
  const dragStartScrollTop = useRef(0);

  const updateThumb = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const { scrollHeight, clientHeight, scrollTop } = el;
    if (scrollHeight <= clientHeight) {
      setNeedsScroll(false);
      return;
    }
    setNeedsScroll(true);
    const trackHeight = trackRef.current?.clientHeight || clientHeight;
    const ratio = clientHeight / scrollHeight;
    setThumbHeight(Math.max(ratio * trackHeight, 20));
    setThumbTop((scrollTop / (scrollHeight - clientHeight)) * (trackHeight - Math.max(ratio * trackHeight, 20)));
  }, []);

  useEffect(() => {
    updateThumb();
  }, [children, updateThumb]);

  const handleScroll = () => updateThumb();

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    dragStartY.current = e.clientY;
    dragStartScrollTop.current = contentRef.current?.scrollTop || 0;
  };

  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e) => {
      const el = contentRef.current;
      const track = trackRef.current;
      if (!el || !track) return;
      const deltaY = e.clientY - dragStartY.current;
      const trackHeight = track.clientHeight;
      const ratio = el.clientHeight / el.scrollHeight;
      const currentThumbHeight = Math.max(ratio * trackHeight, 20);
      const scrollRange = el.scrollHeight - el.clientHeight;
      const trackRange = trackHeight - currentThumbHeight;
      el.scrollTop = dragStartScrollTop.current + (deltaY / trackRange) * scrollRange;
    };
    const handleMouseUp = () => setIsDragging(false);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  return (
    <div className={styles['scroll-wrapper']}>
      <div
        ref={contentRef}
        className={styles['scroll-content']}
        onScroll={handleScroll}
      >
        <p className={styles['card-description']}>{children}</p>
      </div>
      {needsScroll && (
        <div ref={trackRef} className={styles['scroll-track']}>
          <div
            className={styles['scroll-thumb']}
            style={{ height: thumbHeight, transform: `translateY(${thumbTop}px)` }}
            onMouseDown={handleMouseDown}
          />
        </div>
      )}
    </div>
  );
};

const LaunchpadCard = ({ icon, iconStyle, title, description, to, disabled, scrollableDescription }) => {
  const navigate = useNavigate();

  return (
    <div
      className={clsx(styles['card'], disabled && styles['card--disabled'])}
      onClick={() => navigate(to)}
    >
      <div className={styles['card-content']}>
        <div className={styles['card-icon']}>
          <img src={icon} alt={title} style={iconStyle} />
        </div>
        <h3 className={styles['card-title']}>{title}</h3>
        {scrollableDescription ? (
          <ScrollableDescription>{description}</ScrollableDescription>
        ) : (
          <p className={styles['card-description']}>{description}</p>
        )}
      </div>
      <div className={styles['card-arrow-row']}>
        <div className={styles['card-arrow']}>
          <svg width="22" height="22" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 9H15M15 9L10 4M15 9L10 14" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>
    </div>
  );
};

export default LaunchpadCard;
